import {Fragment, SetStateAction, Suspense, useEffect, useState} from 'react'

import {lazily} from 'react-lazily'

import {useAppDispatch, useAppSelector} from '@App/hooks'
import {MODAL_TYPES} from '@App/redux/slices/modals/enums'
import {closeModal, selectModalStack} from '@App/redux/slices/modals/ModalSlice'

import {ProblemGamblerModal} from '@UI/crypto_exchange/desktop/components/modals'
import {EditAlertModal} from '@UI/crypto_exchange/desktop/components/modals/EditAlertModal'
import {RemoveAlertModal} from '@UI/crypto_exchange/desktop/components/modals/RemoveAlertModal'
import TermAndConditionsModal from '@UI/crypto_exchange/desktop/components/modals/TermAndConditionsModal'

import {LoaderBlocks} from '../../common/Loader/components/LoaderBlocks'
import {AccountCreatedModal} from '../AccountCreatedModal'
import {AddMarketsToWhatchlistModal} from '../AddMarketsToWhatchlist'
import {AddWatchlistsModal} from '../AddWhatchlist'
import {ChooseAccountCurrencyModal} from '../ChooseAccountCurrencyModal'
import {EditWatchlistsModal} from '../EditWatchlists'
import {ModalTypes} from '../modalTypes'
import {SendReportModal} from '../SendReportModal'
import {DeleteAccountModal} from "@UI/crypto_exchange/desktop/components/modals/DeleteAccountModal";

const {
  ConfirmModal,
  LoginModal,
  CloseAccountModal,
  ForgotPasswordModal,
  RegistrationModal,
  ForgotUsernameModal,
  CloseAccountMultipleModal,
  CheckMonthStatsAfterLoginModal,
  ExcludeKYCModal,
  FreezePeriodModal,
  InactivityNoticeModal,
  UpdateTermsAndConditionsModal,
  SessionTimeoutModal,
  RealityCheckPeriodModal,
  TraidingOptionsModal,
  PersonalLimitModal,
  UserSettingsModal,
  MarketsListModal,
  RealityChecksModal,
  PlaySafeSessionTimeoutModal,
  SelfExclusionLimitModal,
  UpdateDocumentsModal,
} = lazily(() => import('@UI/crypto_exchange/desktop/components/modals'))

const BASE_Z_INDEX = 500
const STACK_Z_INDEX = 600
const Z_INDEX_STEP = 10

export default function ModalContainer() {
  const dispatch = useAppDispatch()

  const { current } = useAppSelector((state) => state.modals)
  const stack = useAppSelector(selectModalStack)

  const hasAnyModal = !!current || stack.length > 0

  useEffect(() => {
    if (hasAnyModal) {
      document.body.classList.add('overflow')
    } else {
      document.body.classList.remove('overflow')
    }
  }, [hasAnyModal])
  const isOpen = !!current || stack.length > 0

  const [scrollBarWidth, setScrollBarWidth] = useState<SetStateAction<number | null>>(null)

  const handleCloseModal = (modalType?: MODAL_TYPES) => {
    console.log(modalType)
    dispatch(closeModal(modalType))
  }

  const showScroll = () => {
    document.body.classList.remove('overflow')
    document.body.style.paddingRight = ''
  }

  const hideScroll = () => {
    document.body.classList.add('overflow')
    document.body.style.paddingRight = `${scrollBarWidth}px`
  }

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => hideScroll(), 10)
    } else {
      setTimeout(() => showScroll(), 10)
    }
  }, [isOpen])

  const renderModal = (modalType: MODAL_TYPES, data: any, zIndex: number, onClose: () => void) => {
    const commonProps: ModalTypes = { isOpen: true, setIsOpen: onClose, styles: { overlay: { zIndex } } }

    switch (modalType) {
      case MODAL_TYPES.PLAY_SAFE_DEPOSIT_LIMIT_MODAL:
      case MODAL_TYPES.PLAY_SAFE_BET_LIMIT_MODAL:
      case MODAL_TYPES.PLAY_SAFE_LOSS_LIMIT_MODAL:
        return <PersonalLimitModal {...commonProps} {...data} popupData={data} />

      case MODAL_TYPES.PLAY_SAFE_REALITY_CHECK_MODAL:
        return <RealityChecksModal {...commonProps} {...data} />

      case MODAL_TYPES.PROBLEM_GAMBLER_MODAL:
        return <ProblemGamblerModal {...commonProps} {...data} />

      case MODAL_TYPES.PLAY_SAFE_SESSION_TIMEOUT_MODAL:
        return <PlaySafeSessionTimeoutModal {...commonProps} {...data} />

      case MODAL_TYPES.PLAY_SAFE_SELF_EXCLUSION_MODAL:
        return <SelfExclusionLimitModal {...commonProps} {...data} />

      case MODAL_TYPES.REALITY_CHECK_PERIOD_MODAL:
        return <RealityCheckPeriodModal {...commonProps} {...data} />

      case MODAL_TYPES.SESSION_TIMEOUT_MODAL:
        return <SessionTimeoutModal {...commonProps} {...data} popupData={data} />

      case MODAL_TYPES.UPDATE_TERMS_AND_CONDITIONS_MODAL:
        return <UpdateTermsAndConditionsModal {...commonProps} {...data} popupData={data} />

      case MODAL_TYPES.FREEZE_PERIOD_MODAL:
        return <FreezePeriodModal {...commonProps} {...data} />

      case MODAL_TYPES.INACTIVITY_NOTICE_MODAL:
        return <InactivityNoticeModal {...commonProps} {...data} />

      case MODAL_TYPES.CLOSE_ACCOUNT_MODAL:
        return <CloseAccountModal {...commonProps} {...data} />

      case MODAL_TYPES.CLOSE_ACCOUNT_MULTIPLE_MODAL:
        return <CloseAccountMultipleModal {...commonProps} {...data} />

      case MODAL_TYPES.LOGIN_MODAL:
        return <LoginModal {...commonProps} {...data} />

      case MODAL_TYPES.REGISTRATION_MODAL:
        return <RegistrationModal {...commonProps} {...data} />

      case MODAL_TYPES.FORGOT_USERNAME_MODAL:
        return <ForgotUsernameModal {...commonProps} {...data} />

      case MODAL_TYPES.FORGOT_PASSWORD_MODAL:
        return <ForgotPasswordModal {...commonProps} {...data} />

      case MODAL_TYPES.EXCLUDE_KYC_MODAL:
        return <ExcludeKYCModal {...commonProps} {...data} />

      case MODAL_TYPES.CHECK_MONTH_STATS_AFTER_LOGIN_MODAL:
        return <CheckMonthStatsAfterLoginModal {...commonProps} {...data} />

      case MODAL_TYPES.CONFIRM_MODAL:
        return <ConfirmModal {...commonProps} {...data} />

      case MODAL_TYPES.USER_SETTINGS_MODAL:
        return <UserSettingsModal {...commonProps} {...data} />

      case MODAL_TYPES.MARKETS_LIST_MODAL:
        return <MarketsListModal {...commonProps} {...data} />

      case MODAL_TYPES.TRAIDING_OPTIONS_MODAL:
        return <TraidingOptionsModal {...commonProps} {...data} />

      case MODAL_TYPES.DELETE_ACCOUNT_MODAL:
        return <DeleteAccountModal {...commonProps} {...data} />

      case MODAL_TYPES.TERMS_AND_CONDITIONS:
        return <TermAndConditionsModal {...commonProps} {...data} currentTerm={data} />

      case MODAL_TYPES.CHOOSE_ACCOUNT_CURRENCY_MODAL:
        return <ChooseAccountCurrencyModal {...commonProps} {...data} />

      case MODAL_TYPES.ACCOUNT_CREATED_MODAL:
        return <AccountCreatedModal {...commonProps} {...data} />

      case MODAL_TYPES.SEND_REPORT_MODAL:
        return <SendReportModal {...commonProps} {...data} />

      case MODAL_TYPES.UPDATE_DOCUMENTS_MODAL:
        return <UpdateDocumentsModal {...commonProps} {...data} />

      case MODAL_TYPES.EDIT_WATCHLISTS:
        return <EditWatchlistsModal {...commonProps} {...data} />

      case MODAL_TYPES.ADD_WATCHLIST:
        return <AddWatchlistsModal {...commonProps} {...data} />
      case MODAL_TYPES.ADD_MARKETS_TO_WATCHLIST:
        return <AddMarketsToWhatchlistModal {...commonProps} {...data} />
      case MODAL_TYPES.EDIT_ALERT_MODAL:
        return <EditAlertModal {...commonProps} {...data} />
      case MODAL_TYPES.REMOVE_ALERT_MODAL:
        return <RemoveAlertModal {...commonProps} {...data} />

      default:
        return null
    }
  }

  return (
    <Suspense fallback={<LoaderBlocks />}>
      {current && (
        <Fragment key={current.modalType}>
          {renderModal(current.modalType, current.data, BASE_Z_INDEX, () => handleCloseModal())}
        </Fragment>
      )}

      {stack.map((modal, index) => (
        <Fragment key={modal.modalType}>
          {renderModal(modal.modalType, modal.data, STACK_Z_INDEX + index * Z_INDEX_STEP, () =>
            handleCloseModal(modal.modalType),
          )}
        </Fragment>
      ))}
    </Suspense>
  )
}
